import React, { useState, useEffect } from "react";
import {
  Paper,
  Box,
  Typography,
  Grid,
  Chip,
  Divider,
  Stack,
  Tooltip,
  Tabs,
  Tab,
  CircularProgress
} from "@mui/material";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import EventIcon from "@mui/icons-material/Event";
import UpdateIcon from "@mui/icons-material/Update";
import SyncIcon from "@mui/icons-material/Sync";
import GavelIcon from "@mui/icons-material/Gavel";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { useSiteVarsFunctions } from "../hooks/use-site-vars-functions";
import { getFixturesResource } from "../services/fixtures.service";

export const LeaderboardStatusPanel = () => {
  const {
    getCurrentGameweekEvent,
    getNextGameweekEvent,
    getScoresUpdatedTime,
    getCurrentWindow,
    getPhase,
    getBiddingOpen,
    getOffersOpen,
    getTeamById
  } = useSiteVarsFunctions();

  const currentEvent = getCurrentGameweekEvent();
  const nextEvent = getNextGameweekEvent();
  const currentWeek = currentEvent?.id || 1;
  const nextWeek = nextEvent?.id || (currentWeek + 1);

  const scoresUpdatedTime = getScoresUpdatedTime();
  const currentWindow = getCurrentWindow();
  const currentPhase = getPhase();
  const biddingOpen = getBiddingOpen();
  const offersOpen = getOffersOpen();

  // Fixtures state
  const [selectedTab, setSelectedTab] = useState(0); // 0 = Current GW, 1 = Next GW
  const [currentFixtures, setCurrentFixtures] = useState([]);
  const [nextFixtures, setNextFixtures] = useState([]);
  const [isLoadingFixtures, setIsLoadingFixtures] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const loadFixtures = async () => {
      setIsLoadingFixtures(true);
      try {
        const [resCurrent, resNext] = await Promise.all([
          getFixturesResource(currentWeek),
          getFixturesResource(nextWeek)
        ]);
        if (isMounted) {
          if (resCurrent.data) setCurrentFixtures(resCurrent.data);
          if (resNext.data) setNextFixtures(resNext.data);
        }
      } catch (e) {
        console.error("Error loading fixtures:", e);
      } finally {
        if (isMounted) setIsLoadingFixtures(false);
      }
    };

    loadFixtures();
    return () => { isMounted = false; };
  }, [currentWeek, nextWeek]);

  // Format date helper
  const formatDate = (isoString) => {
    if (!isoString) return "TBD";
    try {
      const d = new Date(isoString);
      return d.toLocaleDateString("en-GB", {
        weekday: "short",
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch {
      return isoString;
    }
  };

  // Format kickoff time for fixtures
  const formatKickoffTime = (isoString) => {
    if (!isoString) return "TBD";
    try {
      const d = new Date(isoString);
      return d.toLocaleDateString("en-GB", {
        weekday: "short",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch {
      return isoString;
    }
  };

  // Relative time helper
  const getRelativeTime = (isoString) => {
    if (!isoString) return "";
    try {
      const target = new Date(isoString).getTime();
      const now = Date.now();
      const diffMs = target - now;
      if (diffMs <= 0) return "(Deadline passed)";
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const days = Math.floor(diffHours / 24);
      const hours = diffHours % 24;
      if (days > 0) return `(in ${days}d ${hours}h)`;
      return `(in ${hours}h)`;
    } catch {
      return "";
    }
  };

  // Format last updated timestamp
  const formatLastUpdated = (timeVal) => {
    if (!timeVal) return "Never";
    try {
      let dateObj;
      if (timeVal._seconds !== undefined) {
        dateObj = new Date(timeVal._seconds * 1000 + (timeVal._nanoseconds || 0) / 1000000);
      } else {
        dateObj = new Date(timeVal);
      }
      return dateObj.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch {
      return "Unknown";
    }
  };

  // Determine current gameweek status
  let statusLabel = "Upcoming Kickoff";
  let statusColor = "info";
  if (currentEvent) {
    if (currentEvent.finished) {
      statusLabel = "Matches Completed";
      statusColor = "warning";
    } else if (currentEvent.is_current) {
      statusLabel = "Live Matches in Progress";
      statusColor = "success";
    } else {
      statusLabel = "Upcoming Kickoff";
      statusColor = "primary";
    }
  }

  const activeFixtures = selectedTab === 0 ? currentFixtures : nextFixtures;

  return (
    <Paper
      elevation={2}
      sx={{
        mt: 2,
        mb: 2,
        p: 1.5,
        borderRadius: 2,
        backgroundColor: "background.paper",
        border: "1px solid rgba(0, 0, 0, 0.08)"
      }}
    >
      {/* Header Row */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          mb: 1.25
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <SportsSoccerIcon color="primary" fontSize="small" />
          <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: "0.95rem" }}>
            League Status & Schedule
          </Typography>
        </Box>
        <Tooltip title="Current active competition status">
          <Chip
            size="small"
            label={statusLabel}
            color={statusColor}
            variant="filled"
            sx={{ fontWeight: "medium", height: 22, fontSize: "0.72rem" }}
          />
        </Tooltip>
      </Box>

      <Divider sx={{ mb: 1.5 }} />

      {/* Main Metadata 2x2 Grid on Mobile, 4 columns on Desktop */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
          gap: 1.5,
          width: "100%",
          boxSizing: "border-box"
        }}
      >
        {/* Tile 1: Current Gameweek */}
        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <EventIcon sx={{ fontSize: 15, color: "text.primary" }} />
            <Typography variant="caption" color="text.primary" fontWeight="bold" sx={{ fontSize: "0.68rem" }} noWrap>
              CURRENT GW - {currentWeek}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.72rem" }} noWrap display="block">
            Start: <b>{formatDate(currentEvent?.deadline_time)}</b>
          </Typography>
        </Stack>

        {/* Tile 2: Next Gameweek */}
        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <UpdateIcon sx={{ fontSize: 15, color: "text.primary" }} />
            <Typography variant="caption" color="text.primary" fontWeight="bold" sx={{ fontSize: "0.68rem" }} noWrap>
              NEXT GW - {nextWeek}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.72rem" }} noWrap display="block">
            <b>{formatDate(nextEvent?.deadline_time)}</b>{" "}
            <span style={{ color: "#1976d2", fontWeight: 500 }}>
              {getRelativeTime(nextEvent?.deadline_time)}
            </span>
          </Typography>
        </Stack>

        {/* Tile 3: Live Score Sync */}
        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <SyncIcon sx={{ fontSize: 15, color: "text.primary" }} />
            <Typography variant="caption" color="text.primary" fontWeight="bold" sx={{ fontSize: "0.68rem" }} noWrap>
              LIVE SCORE SYNC
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.72rem" }} noWrap display="block">
            <b>{formatLastUpdated(scoresUpdatedTime)}</b>
          </Typography>
        </Stack>

        {/* Tile 4: Market & Transfers */}
        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <GavelIcon sx={{ fontSize: 15, color: "text.primary" }} />
            <Typography variant="caption" color="text.primary" fontWeight="bold" sx={{ fontSize: "0.68rem" }} noWrap>
              MARKET & TRANSFERS
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap", mt: 0.25 }}>
            <Chip
              size="small"
              label={biddingOpen ? "Bids Open" : "Bids Closed"}
              color={biddingOpen ? "success" : "default"}
              variant="outlined"
              sx={{ height: 18, fontSize: "0.65rem", px: 0.25 }}
            />
            <Chip
              size="small"
              label={offersOpen ? "Offers Open" : "Offers Closed"}
              color={offersOpen ? "success" : "default"}
              variant="outlined"
              sx={{ height: 18, fontSize: "0.65rem", px: 0.25 }}
            />
          </Box>
        </Stack>
      </Box>

      <Divider sx={{ my: 1.75 }} />

      {/* Fixtures Schedule Section */}
      <Box>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1.25,
            gap: 1
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <CalendarMonthIcon fontSize="small" color="primary" />
            <Typography variant="subtitle2" fontWeight="bold" sx={{ fontSize: "0.88rem" }}>
              Premier League Fixtures
            </Typography>
          </Box>

          <Tabs
            value={selectedTab}
            onChange={(e, val) => setSelectedTab(val)}
            textColor="primary"
            indicatorColor="primary"
            sx={{
              minHeight: 28,
              '& .MuiTab-root': {
                minHeight: 28,
                py: 0.25,
                px: 1.25,
                fontSize: "0.75rem",
                fontWeight: "bold",
                textTransform: "none"
              }
            }}
          >
            <Tab label={`GW ${currentWeek} (Current)`} />
            <Tab label={`GW ${nextWeek} (Next)`} />
          </Tabs>
        </Box>

        {isLoadingFixtures ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={22} />
          </Box>
        ) : activeFixtures.length === 0 ? (
          <Typography variant="caption" color="text.secondary">
            No fixtures scheduled for this gameweek.
          </Typography>
        ) : (
          /* Strictly 2-column mobile & small-tablet grid (xs, sm: repeat(2, 1fr)), 4-column tablet/laptop (md), 5-column desktop (lg) */
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, 1fr)",
                sm: "repeat(2, 1fr)",
                md: "repeat(4, 1fr)",
                lg: "repeat(5, 1fr)"
              },
              gap: 1,
              width: "100%",
              boxSizing: "border-box"
            }}
          >
            {activeFixtures.map((fix) => {
              const homeTeam = getTeamById(fix.team_h) || {};
              const awayTeam = getTeamById(fix.team_a) || {};
              const homeShort = homeTeam.name_short || `T${fix.team_h}`;
              const awayShort = awayTeam.name_short || `T${fix.team_a}`;

              const now = Date.now();
              const kickoff = fix.kickoff_time ? new Date(fix.kickoff_time).getTime() : null;
              const hasKickedOff = kickoff && now >= kickoff;
              const isFinished = Boolean(
                fix.finished ||
                fix.finished_provisional ||
                (fix.minutes >= 90 && fix.started) ||
                (kickoff && now - kickoff >= 115 * 60 * 1000)
              );
              const isLive = (fix.started || hasKickedOff) && !isFinished;
              const isUpcoming = !isLive && !isFinished;

              return (
                <Box
                  key={fix.id}
                  sx={{
                    p: 1,
                    borderRadius: 1.5,
                    backgroundColor: isLive
                      ? "rgba(46, 125, 50, 0.08)"
                      : isFinished
                      ? "rgba(0, 0, 0, 0.04)"
                      : "rgba(0, 0, 0, 0.02)",
                    border: isLive
                      ? "1px solid rgba(46, 125, 50, 0.45)"
                      : isFinished
                      ? "1px solid rgba(0, 0, 0, 0.12)"
                      : "1px solid rgba(0, 0, 0, 0.06)",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: 58,
                    height: "100%",
                    boxSizing: "border-box",
                    width: "100%",
                    minWidth: 0
                  }}
                >
                  {/* Teams & Score Row (Guaranteed Single-Line Horizontal) */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                      px: 0.25
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      noWrap
                      sx={{
                        flex: 1,
                        textAlign: "right",
                        fontSize: "0.82rem",
                        letterSpacing: "0.2px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap"
                      }}
                    >
                      {homeShort}
                    </Typography>

                    <Box
                      sx={{
                        textAlign: "center",
                        px: 0.5,
                        minWidth: 38,
                        flexShrink: 0
                      }}
                    >
                      {isUpcoming ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight="bold"
                          sx={{ fontSize: "0.75rem" }}
                        >
                          v
                        </Typography>
                      ) : (
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          sx={{
                            color: isLive ? "success.main" : "text.primary",
                            fontSize: "0.82rem",
                            whiteSpace: "nowrap"
                          }}
                        >
                          {fix.team_h_score ?? 0}-{fix.team_a_score ?? 0}
                        </Typography>
                      )}
                    </Box>

                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      noWrap
                      sx={{
                        flex: 1,
                        textAlign: "left",
                        fontSize: "0.82rem",
                        letterSpacing: "0.2px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap"
                      }}
                    >
                      {awayShort}
                    </Typography>
                  </Box>

                  {/* Status / Kickoff Badge */}
                  <Box sx={{ display: "flex", justifyContent: "center", mt: 0.4 }}>
                    {isFinished && (
                      <Chip
                        size="small"
                        label="FT"
                        variant="filled"
                        sx={{
                          height: 18,
                          fontSize: "0.65rem",
                          fontWeight: "bold",
                          px: 0.5,
                          backgroundColor: "rgba(0, 0, 0, 0.08)",
                          color: "text.primary"
                        }}
                      />
                    )}
                    {isLive && (
                      <Chip
                        size="small"
                        label={fix.minutes ? `${fix.minutes}'` : "LIVE"}
                        color="success"
                        sx={{ height: 18, fontSize: "0.65rem", fontWeight: "bold", px: 0.5 }}
                      />
                    )}
                    {isUpcoming && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: "0.68rem", fontWeight: 500, whiteSpace: "nowrap" }}
                      >
                        {formatKickoffTime(fix.kickoff_time)}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Paper>
  );
};
